import { z } from "zod";
/*
 * The extraction ledger for Xero attachment documents as a running turn uses it through the tenant
 * API. The connector keeps the same rows as it reads recent documents; a turn reads the ones it is
 * asked for. The tenant resolves each attachment within one of the run's organisations, checks the
 * run's warehouse grant covers Xero attachments, and keeps one row per file: a turn claims the row
 * before it spends Reducto credits, records the submitted job, and completes, skips or fails it.
 * Each request carries the run's lease and is answered only while that attempt holds the run.
 */
const id = z.string().trim().min(1).max(200);
const scope = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
    organisationId: id,
    /** The run's warehouse grant, checked before any row is read or written. */
    grant: z.unknown(),
};
const attachmentId = z.string().trim().min(1).max(200);
const revision = { ...scope, attachmentId, sourcePayloadHash: z.string().regex(/^[a-f0-9]{64}$/u) };
const ownedRevision = { ...revision, claimToken: z.string().uuid() };
const fileFacts = {
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSizeBytes: z.number().int().nonnegative().nullable(),
};
/** Attachments one request may open. */
export const WORKER_XERO_DOCUMENT_MAX_ATTACHMENTS = 10;
/** The most text one opened row carries; the row says when its text ran past this. */
export const WORKER_XERO_DOCUMENT_MAX_TEXT_CHARACTERS = 20_000;
export const workerXeroDocumentRequestSchema = z.discriminatedUnion("operation", [
    /** Resolves each attachment and returns its ledger row, if any. */
    z.object({
        ...scope, operation: z.literal("open"),
        attachmentIds: z.array(attachmentId).min(1).max(WORKER_XERO_DOCUMENT_MAX_ATTACHMENTS),
        maxTextCharacters: z.number().int().positive().max(WORKER_XERO_DOCUMENT_MAX_TEXT_CHARACTERS),
    }).strict(),
    /** Claims the row for a new submission unless a fresh one is in flight or the file is already read. */
    z.object({ ...revision, operation: z.literal("claim"), ...fileFacts }).strict(),
    z.object({ ...ownedRevision, operation: z.literal("submitted"), documentInput: z.string().min(1), jobId: id }).strict(),
    z.object({
        ...ownedRevision, operation: z.literal("completed"), jobId: id,
        payload: z.record(z.string(), z.unknown()), chunks: z.array(z.unknown()), extractedText: z.string(),
        usage: z.record(z.string(), z.unknown()).nullable(), numPages: z.number().int().nonnegative().nullable(),
        credits: z.number().nonnegative().nullable(), studioLink: z.string().nullable(), pdfUrl: z.string().nullable(),
    }).strict(),
    z.object({ ...ownedRevision, operation: z.literal("skipped"), filename: z.string().nullable(), errorMessage: z.string() }).strict(),
    /** Fails the submission that named this job, or the claim that never recorded one (null). */
    z.object({ ...ownedRevision, operation: z.literal("failed"), jobId: id.nullable(), errorMessage: z.string() }).strict(),
]);
export const workerXeroDocumentExtractionSchema = z.object({
    sourcePayloadHash: z.string(),
    claimToken: z.string().uuid(),
    status: z.enum(["pending", "submitted", "completed", "skipped", "failed"]),
    reductoJobId: z.string().nullable(),
    extractedText: z.string().nullable(),
    textTruncated: z.boolean(),
    numPages: z.number().nullable(),
    credits: z.number().nullable(),
    errorMessage: z.string().nullable(),
    attempts: z.number().int(),
    submittedAt: z.number().nullable(),
    failedAt: z.number().nullable(),
    updatedAt: z.number(),
}).strict();
export const workerXeroDocumentSchema = z.object({
    attachmentId,
    /** The file as the ledger keeps it: id, name, media type, size and parent. */
    file: z.record(z.string(), z.unknown()),
    sourcePayloadHash: z.string(),
    extraction: workerXeroDocumentExtractionSchema.nullable(),
}).strict();
export const workerXeroDocumentResponseSchema = z.discriminatedUnion("operation", [
    z.object({
        operation: z.literal("open"),
        documents: z.array(workerXeroDocumentSchema),
        /** Attachment ids this organisation's Xero connection has not listed. */
        notFound: z.array(attachmentId),
    }).strict(),
    z.object({ operation: z.literal("claim"), claimed: z.boolean(), claimToken: z.string().uuid().nullable() }).strict(),
    z.object({ operation: z.literal("submitted"), applied: z.boolean() }).strict(),
    z.object({ operation: z.literal("completed"), applied: z.boolean() }).strict(),
    z.object({ operation: z.literal("skipped"), applied: z.boolean() }).strict(),
    z.object({ operation: z.literal("failed"), applied: z.boolean() }).strict(),
]);
/** Stable refusal codes; the executor turns each into the tool error the agent sees. */
export const WORKER_XERO_DOCUMENT_REFUSALS = {
    leaseNotHeld: "xero_document_lease_not_held",
    documentNotFound: "xero_document_not_found",
    revisionChanged: "xero_document_revision_changed",
};
